import React, { useState } from "react";
import {
  Alert,
  Button,
  Form,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { useUsersApi } from "../../services/users";
import { notifySuccess } from "../../services/notify";
import {
  EMPTY_PASSWORD,
  GeneratedPassword,
  PasswordChoice,
  passwordToSend,
} from "./PasswordFields";

// Sets a new password for someone else, without their current one -- for a
// user who is locked out. `user` is the row being reset.
export const ResetPasswordModal = ({ isOpen, user, toggle }) => {
  const usersApi = useUsersApi();
  const [password, setPassword] = useState(EMPTY_PASSWORD);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [generated, setGenerated] = useState(null);

  // Clears everything, so no password outlives the dialog.
  const finish = () => {
    setPassword(EMPTY_PASSWORD);
    setError(null);
    setGenerated(null);
    toggle();
  };

  // What the user can trigger; not while a request is in flight.
  const close = () => {
    if (!submitting) finish();
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    const chosen = passwordToSend(password);
    if (chosen.error) {
      setError(chosen.error);
      return;
    }

    setSubmitting(true);
    let result;
    try {
      result = await usersApi.resetPassword(user.id, chosen.password);
    } catch (caught) {
      setError(caught.message);
      setSubmitting(false);
      return;
    }
    setSubmitting(false);

    if (result.generatedPassword) {
      setGenerated(result.generatedPassword);
    } else {
      notifySuccess(`Reset the password of ${user.email}`);
      finish();
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={close} centered>
      {generated ? (
        <>
          <ModalHeader toggle={close}>Password reset</ModalHeader>
          <ModalBody style={{ fontSize: "13px" }}>
            <GeneratedPassword email={user.email} password={generated} />
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={close}
              style={{ background: "#4A42A0", borderColor: "#4A42A0" }}
            >
              Done
            </Button>
          </ModalFooter>
        </>
      ) : (
        <Form onSubmit={onSubmit} data-testid="reset-password-form">
          <ModalHeader toggle={close}>Reset password</ModalHeader>
          <ModalBody style={{ fontSize: "13px" }}>
            <p>
              Set a new password for <strong>{user.email}</strong>. Their current
              password stops working immediately.
            </p>
            <PasswordChoice
              value={password}
              onChange={(value) => {
                setPassword(value);
                setError(null);
              }}
              disabled={submitting}
            />
            {/* Logto 1.26 has no call that ends another user's sessions. */}
            <p className="mt-3 mb-0" style={{ color: "#575757" }}>
              Anyone already signed in as this user stays signed in until that
              session ends.
            </p>
            {error ? (
              <Alert
                color="danger"
                className="mt-3 mb-0"
                style={{ fontSize: "13px" }}
                data-testid="reset-password-error"
              >
                {error}
              </Alert>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={close} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              style={{ background: "#4A42A0", borderColor: "#4A42A0" }}
            >
              {submitting ? "Resetting..." : "Reset password"}
            </Button>
          </ModalFooter>
        </Form>
      )}
    </Modal>
  );
};
